import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import EmojiPicker from "emoji-picker-react";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";
import CountryDropdownSelection from "components/forms/CountryDropdownSelection";
import Backdrop from "components/other/Backdrop";
import ModalWindow from "components/other/ModalWindow";
import ButtonPrimary from "components/buttons/ButtonPrimary";

import emailBookIcon from "assets/img/icons/icons8-address-book.svg";
import bulbIcon from "assets/img/icons/tip-bulb.svg";
import smileyIcon from "assets/img/icons/icons8-smiley.svg";
import Checkbox from "components/forms/Checkbox";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import MailingListEmailFormContainer from "./MailingListEmailFormContainer";

const CreateMailingList = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TitleIcon = styled.div`
  display: inline-block;
  padding-right: 6px;

  transform: translateY(4.5px);

  width: 28px;
  min-width: 28px;
`;

const Text = styled.div`
  margin-top: ${(props) => (props.margintop ? props.margintop : 0)};

  line-height: 130%;
`;

const FormBox = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  flex-direction: column;
`;

const SubTitle = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_LARGE};

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TipBox = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
  padding: ${css.BOX_PADDING_SECONDARY};
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
  align-items: flex-start;
`;

const TipWrap = styled.div`
  display: flex;
  align-items: flex-start;
`;

const TipIcon = styled.div`
  width: 22px;
  min-width: 22px;
  height: 22px;
`;

const TipText = styled.div`
  padding-left: 12px;

  flex: 1;
`;

const TipTextStrong = styled.span`
  font-weight: 600;
`;

const SignatureInputBox = styled.div`
  width: 100%;
  padding: 0;
  box-sizing: border-box;

  background: ${css.DARK_BLUE};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 25px;

  font-family: "Roboto";
  font-weight: 300;
  font-size: 15px;
  color: #fff;
  line-height: 125%;

  background: ${css.DARK_BLUE};
  border: 0px;
  border-radius: ${css.BOX_RADIUS_SECONDARY};
  resize: none;
`;

const TextInputFooter = styled.div`
  height: 50px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CharacterCounter = styled.div`
  position: relative;
  margin-left: 25px;
`;

const EmptyCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  transform: translate(-50%, -50%);

  border-radius: 50%;
  background: rgb(255, 255, 255, 0.07);
`;

const OuterCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 22px;
  height: 22px;
  transform: translate(-50%, -50%);

  border-radius: 50%;
  background: ${(props) => (props.atmax ? css.ERROR_RED : css.BRAND_INDIGO)};
  mask: conic-gradient(
    from 0deg,
    #0000,
    #000 1deg ${(props) => props.angle}deg,
    #0000 calc(${(props) => props.angle}deg + 1deg)
  );
`;

const InnerCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 16px;
  height: 16px;
  transform: translate(-50%, -50%);

  background: ${css.DARK_BOX_BACKGROUND};
  border-radius: 50%;
`;

const RightControls = styled.div`
  padding: 0 18px 0 25px;

  display: flex;
  align-items: center;
`;

const TextAreaIconCircle = styled.div`
  width: 36px;
  min-width: 36px;
  height: 36px;

  border-radius: 18px;

  transition: 0.2s;

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: rgb(255, 255, 255, 0.07);
    cursor: pointer;
  }
`;

const TextAreaIcon = styled.div`
  width: ${(props) => props.width};
  min-width: ${(props) => props.width};
  height: ${(props) => props.width};

  &:hover {
    cursor: pointer;
  }
`;

const FormBottom = styled.div`
  position: relative;
  width: 100%;
`;

const PickerWrap = styled.div`
  position: absolute;
  top: ${css.VERTICAL_BOX_MARGIN};
  right: 0;
  z-index: 1;
`;

const Agreements = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_LARGE};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const AgreeLine = styled.div`
  display: flex;
  gap: 8px;

  &:hover {
    cursor: pointer;
  }
`;

const AgreeText = styled.div``;

const ButtonWrap = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_LARGE};

  display: flex;
`;

const Warning = styled.div`
  margin-top: 7px;

  font-size: 13px;
  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const EmailLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const EmailValue = styled.div`
  padding-left: 4px;
  word-break: break-all;
`;

const ChangeEmailButtonWrap = styled.div``;

const FormModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);
  width: 100%;
  max-width: 520px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

export default ({
  listId,
  name,
  onNameInputChange,
  behalfName,
  onBehalfNameInputChange,
  email,
  behalfAddress,
  onBehalfAddressInputChange,
  onAccountCountryChange,
  warnings,
  signature,
  onSignatureInputChange,
  onSmileyClick,
  pickerOpened,
  onEmojiClick,
  usePickerOutsideClick,
  agreeAddress,
  onAgreeAddressClick,
  agreeConditions,
  onAgreeConditionsClick,
  submitForm,
  submitLoading,
  openEmailForm,
  emailFormOpened,
  closeEmailForm,
  openErrorWindow,
  changeEmail,
}) => {
  const pickerRef = useRef();

  const signatureMaximum = 500;

  const counterAngle = (360 / signatureMaximum) * signature.length;

  usePickerOutsideClick(pickerRef);

  const boxTitle = listId ? "Update Your List" : "Create New List";
  const buttonText = listId ? "Update List" : "Create List";

  return (
    <CreateMailingList>
      <TopContent>
        <Heading>
          <BoxTitle>
            <TitleIcon>
              <img src={emailBookIcon} style={{ width: "100%" }} />
            </TitleIcon>
            {boxTitle}
          </BoxTitle>
          {!listId && (
            <Text>
              To use Lead Capture Pages, you need to create a Mailing List where
              the contacts you collect will be stored. You can have multiple
              lists and segment your leads according to the offer you are
              promoting.
            </Text>
          )}
        </Heading>
      </TopContent>
      <FormBox>
        <InputWithLabel
          label="List Name"
          width="100%"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
          tip="The list name will not be visible to anyone; it is for your reference only."
          tipId="listName"
        >
          <InputText
            type="text"
            name="name"
            value={name}
            placeholder="Enter any list name for your reference"
            onChange={onNameInputChange}
            maxLength={60}
            warning={warnings.includes("name")}
          />
        </InputWithLabel>
        <InputWithLabel
          label="Your Full Name"
          width="100%"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
          tip="Legal requirements mandate that all email correspondence sent to your subscribers must include your real name."
          tipId="behalfName"
        >
          <InputText
            type="text"
            name="behalfName"
            value={behalfName}
            placeholder="Enter your name"
            onChange={onBehalfNameInputChange}
            maxLength={60}
            warning={warnings.includes("behalfName")}
          />
        </InputWithLabel>
        <InputWithLabel
          label="Your Email Address"
          width="100%"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
          tip="This email address will be included in the verification email sent to your subscribers, and they will be able to reply to it."
          tipId="email"
        >
          <EmailLine>
            <EmailValue>{email ? email : "No email set"}</EmailValue>
            <ChangeEmailButtonWrap>
              <ButtonPrimarySmall onClick={openEmailForm}>
                Change Email
              </ButtonPrimarySmall>
            </ChangeEmailButtonWrap>
          </EmailLine>
        </InputWithLabel>
        {warnings.includes("email") && (
          <Warning>The email address is missing or incorrect.</Warning>
        )}
        <SubTitle>Your Residential Address</SubTitle>
        <TipBox>
          <TipWrap>
            <TipIcon>
              <img src={bulbIcon} alt="Tip" style={{ width: "100%" }} />
            </TipIcon>
            <TipText>
              <TipTextStrong>Why do we need your address?</TipTextStrong>{" "}
              According to laws like the GDPR and anti-spam regulations, anyone
              sending marketing emails or newsletters must include their name
              and physical address in each email. Including your address in the
              verification email is a legal obligation and helps build trust
              with your audience.
            </TipText>
          </TipWrap>
        </TipBox>
        <InputWithLabel
          label="Street"
          width="100%"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
        >
          <InputText
            type="text"
            name="street"
            value={behalfAddress.street}
            placeholder="Enter your street"
            onChange={onBehalfAddressInputChange}
            maxLength={60}
            warning={warnings.includes("street")}
          />
        </InputWithLabel>
        <InputWithLabel
          label="City"
          width="100%"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
        >
          <InputText
            type="text"
            name="city"
            value={behalfAddress.city}
            placeholder="Enter your city"
            onChange={onBehalfAddressInputChange}
            maxLength={40}
            warning={warnings.includes("city")}
          />
        </InputWithLabel>
        <InputWithLabel
          label="Postal Code"
          width="100%"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
        >
          <InputText
            type="text"
            name="zipCode"
            value={behalfAddress.zipCode}
            placeholder="Enter your ZIP code"
            onChange={onBehalfAddressInputChange}
            maxLength={10}
            warning={warnings.includes("zipCode")}
          />
        </InputWithLabel>
        <InputWithLabel
          label="Country"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
        >
          <CountryDropdownSelection
            position="bottom"
            onCountrySelect={onAccountCountryChange}
            selectedCountry={behalfAddress.country}
            warning={warnings.includes("country")}
            placeholder="Country"
          />
        </InputWithLabel>
        <SubTitle>Signature</SubTitle>
        <Text margintop="12px">
          The signature will be displayed at the end of the email we send to
          your leads to verify their email address.
        </Text>
        <InputWithLabel
          label="Email Signature (optional)"
          marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
        >
          <SignatureInputBox>
            <TextArea
              name="text"
              value={signature}
              placeholder="Enter your signature"
              maxLength={signatureMaximum}
              onChange={onSignatureInputChange}
            />
            <TextInputFooter>
              <CharacterCounter>
                <EmptyCircle />
                <OuterCircle
                  angle={counterAngle}
                  atmax={signature.length === signatureMaximum ? 1 : 0}
                />
                <InnerCircle />
              </CharacterCounter>
              <RightControls>
                <TextAreaIconCircle onClick={onSmileyClick}>
                  <TextAreaIcon width="20px">
                    <img src={smileyIcon} style={{ width: "100%" }} />
                  </TextAreaIcon>
                </TextAreaIconCircle>
              </RightControls>
            </TextInputFooter>
          </SignatureInputBox>
          <FormBottom>
            <PickerWrap ref={pickerRef}>
              <EmojiPicker
                open={pickerOpened}
                onEmojiClick={onEmojiClick}
                autoFocusSearch={false}
                theme="dark"
                width={280}
                height={310}
              />
            </PickerWrap>
          </FormBottom>
        </InputWithLabel>
        <Agreements>
          <div>
            <AgreeLine onClick={onAgreeAddressClick}>
              <Checkbox checked={agreeAddress} />
              <AgreeText>
                I confirm that I have entered my real address where I can be
                reached.
              </AgreeText>
            </AgreeLine>
            {warnings.includes("agreeAddress") && (
              <Warning>
                Check the checkbox to indicate your agreement with the statement
                above.
              </Warning>
            )}
          </div>
          <div>
            <AgreeLine onClick={onAgreeConditionsClick}>
              <Checkbox checked={agreeConditions} />
              <AgreeText>
                I confirm that I will not send unsolicited emails to unverified
                addresses, and I will not promote adult content, fraudulent
                offers, pyramid schemes, HYIP offers, or any content that could
                be considered illegal.
              </AgreeText>
            </AgreeLine>
            {warnings.includes("agreeConditions") && (
              <Warning>
                Check the checkbox to indicate your agreement with the statement
                above.
              </Warning>
            )}
          </div>
        </Agreements>
        <ButtonWrap>
          <ButtonPrimary onClick={submitForm} disabled={submitLoading}>
            {buttonText}
          </ButtonPrimary>
        </ButtonWrap>
        {warnings.length !== 0 && (
          <Warning>
            There are errors in the form. Please check the entered information.
          </Warning>
        )}
      </FormBox>
      {emailFormOpened && (
        <>
          <Backdrop
            onClick={closeEmailForm}
            parameters="emailForm"
            background="rgba(0, 0, 0, 0.7)"
          />
          <FormModalWrap>
            <ModalWindow
              handleModalClose={closeEmailForm}
              fullScreenHeight={true}
              title="Select List Email Address"
            >
              <MailingListEmailFormContainer
                email={email}
                openErrorWindow={openErrorWindow}
                changeEmail={changeEmail}
                closeEmailForm={closeEmailForm}
              />
            </ModalWindow>
          </FormModalWrap>
        </>
      )}
    </CreateMailingList>
  );
};
